import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  CheckCircle,
  CloudDownload,
  ErrorOutline,
  Search,
} from "@material-ui/icons";
import MUIDataTable from "mui-datatables";

import SchainAudit from "../SchainAudit/SchainAudit";
import { downloadBlob } from "../SchainAudit/auditUtils";

const API_TOKEN = "2b0019cc43f5e0653066";
const API_BASE = "http://mobileappscrapper.herokuapp.com/js/https://open.sincera.io/api";

function parseSellersJson(rawText) {
  let parsed;

  try {
    parsed = JSON.parse(rawText);
  } catch (error) {
    throw new Error("Invalid JSON. Paste a valid sellers.json payload.");
  }

  if (!parsed || !Array.isArray(parsed.sellers)) {
    throw new Error("The JSON is valid but does not contain a sellers array.");
  }

  return parsed;
}

function buildSummary(parsed) {
  const sellers = parsed.sellers;
  const counts = {
    types: {},
    ids: {},
    names: {},
    domains: {},
  };

  sellers.forEach((seller) => {
    const type = String(seller.seller_type || "").toUpperCase();
    const sellerId = String(seller.seller_id || "");
    const name = String(seller.name || "");
    const domain = String(seller.domain || "");

    counts.types[type] = (counts.types[type] || 0) + 1;
    counts.ids[sellerId] = (counts.ids[sellerId] || 0) + 1;
    counts.names[name] = (counts.names[name] || 0) + 1;
    counts.domains[domain] = (counts.domains[domain] || 0) + 1;
  });

  const toRows = (collection) =>
    Object.entries(collection)
      .sort((left, right) => right[1] - left[1])
      .map(([label, instances]) => [label || "(empty)", instances]);

  return {
    totalSellers: sellers.length,
    duplicateIds: Object.entries(counts.ids).filter(([key, value]) => key && value > 1).length,
    duplicateNames: Object.entries(counts.names).filter(([key, value]) => key && value > 1).length,
    typeRows: toRows(counts.types),
    idRows: toRows(counts.ids),
    nameRows: toRows(counts.names),
    domainRows: toRows(counts.domains),
  };
}

function validateSellerEntries(parsed) {
  const sellers = parsed.sellers;
  const errors = [];
  const sellerIdCounts = {};

  sellers.forEach((seller, index) => {
    const sellerId = seller.seller_id;
    const displayId = sellerId || seller.name || `row ${index + 1}`;
    const sellerType = String(seller.seller_type || "").toLowerCase();
    const isConfidential = Number(seller.is_confidential) === 1;

    if (!seller.name && !isConfidential) {
      errors.push(`Seller ${displayId}: name is missing.`);
    }

    if (!sellerId) {
      errors.push(`Seller ${displayId}: seller_id is missing.`);
    } else if (typeof sellerId !== "string") {
      errors.push(`Seller ${displayId}: seller_id must be a string.`);
    } else {
      sellerIdCounts[sellerId] = (sellerIdCounts[sellerId] || 0) + 1;
    }

    if (!["publisher", "intermediary", "both"].includes(sellerType)) {
      errors.push(`Seller ${displayId}: seller_type "${seller.seller_type || ""}" is invalid.`);
    }

    if (!isConfidential && !seller.domain) {
      errors.push(`Seller ${displayId}: domain is required when is_confidential is 0.`);
    }
  });

  Object.entries(sellerIdCounts).forEach(([sellerId, count]) => {
    if (count > 1) {
      errors.push(`Duplicate seller_id detected: ${sellerId}.`);
    }
  });

  return errors;
}

function exportSellersCsv(parsed) {
  const sellers = parsed.sellers || [];
  if (sellers.length === 0) {
    throw new Error("No seller rows were found to export.");
  }

  const headers = [
    "seller_id",
    "is_confidential",
    "seller_type",
    "is_passthrough",
    "name",
    "domain",
    "comment",
    "ext",
  ];

  const escapeCsv = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    if (/[",\n\r]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const rows = sellers.map((seller) =>
    headers.map((header) => escapeCsv(header === "ext" ? JSON.stringify(seller.ext || "") : seller[header])),
  );

  const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\r\n");
  downloadBlob("sellers.csv", csv, "text/csv;charset=utf-8");
}

function StatCard({ color, label, value }) {
  return (
    <Box
      style={{
        minWidth: 110,
        padding: "12px 16px",
        borderRadius: 10,
        background: `${color}18`,
        border: `1px solid ${color}33`,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" style={{ color, margin: 0 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
    </Box>
  );
}

function tableOptions(filename) {
  return {
    filterType: "checkbox",
    selectableRows: "none",
    responsive: "standard",
    rowsPerPage: 10,
    downloadOptions: {
      filename: `${filename}_${new Date().toISOString().slice(0, 10)}.csv`,
    },
    textLabels: {
      body: {
        noMatch: "Run an analysis first.",
      },
    },
  };
}

function prettyKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function RenderValue({ value }) {
  if (value === null || value === undefined) {
    return (
      <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
        None
      </Typography>
    );
  }

  if (typeof value !== "object") {
    return <Typography variant="body2">{String(value)}</Typography>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Typography variant="caption" color="textSecondary">[]</Typography>;
    }

    return (
      <Box style={{ marginLeft: 12, borderLeft: "2px solid #eee", paddingLeft: 10 }}>
        {value.map((item, index) => (
          <Box key={index} mb={0.5}>
            <RenderValue value={item} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box component="dl" style={{ margin: 0 }}>
      {Object.entries(value).map(([key, nestedValue]) => (
        <Box key={key} mb={1.5}>
          <Typography
            component="dt"
            variant="caption"
            style={{
              fontWeight: 700,
              color: "#fe5000",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {prettyKey(key)}
          </Typography>
          <Typography component="dd" style={{ marginLeft: 8 }}>
            <RenderValue value={nestedValue} />
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function OpenSinceraExplorer() {
  const [mode, setMode] = useState("ecosystem");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const requiresInput = mode === "publisherId" || mode === "publisherDomain";

  async function fetchData(event) {
    if (event) {
      event.preventDefault();
    }

    setLoading(true);
    setData(null);
    setError("");

    const urlMap = {
      ecosystem: `${API_BASE}/ecosystem`,
      publisherId: `${API_BASE}/publishers?id=${input.trim()}`,
      publisherDomain: `${API_BASE}/publishers?domain=${input.trim()}`,
    };

    try {
      const response = await fetch(urlMap[mode], {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = null;
      }

      if (response.status === 404) {
        setError("No results were found for this query.");
      } else if (!response.ok) {
        setError(payload && payload.error ? String(payload.error) : "The OpenSincera request failed.");
      } else if (payload && payload.error) {
        setError(String(payload.error));
      } else {
        setData(payload);
      }
    } catch (requestError) {
      setError("Network error. Check the connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 10 }}>
        <Search style={{ color: "#fe5000" }} />
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          OpenSincera API Explorer
        </Typography>
      </Box>

      <Tabs
        value={mode}
        onChange={(_, nextMode) => {
          setMode(nextMode);
          setInput("");
          setData(null);
          setError("");
        }}
        indicatorColor="secondary"
        textColor="secondary"
        style={{ marginBottom: 16, borderBottom: "1px solid #e0e0e0" }}
      >
        <Tab value="ecosystem" label="Ecosystem Data" />
        <Tab value="publisherId" label="Publisher by ID" />
        <Tab value="publisherDomain" label="Publisher by Domain" />
      </Tabs>

      <Box component="form" onSubmit={fetchData} mb={2}>
        {requiresInput ? (
          <TextField
            label={mode === "publisherId" ? "Publisher ID" : "Publisher Domain"}
            placeholder={mode === "publisherId" ? "e.g. 1" : "e.g. businessinsider.com"}
            variant="outlined"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            fullWidth
            required
            size="small"
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <Button
          type={requiresInput ? "submit" : "button"}
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={requiresInput ? undefined : fetchData}
          startIcon={<Search />}
        >
          {loading ? "Searching..." : requiresInput ? "Search" : "Get Ecosystem Data"}
        </Button>
      </Box>

      {error ? (
        <Box
          display="flex"
          alignItems="center"
          style={{
            gap: 8,
            color: "#e74c3c",
            marginBottom: 12,
            padding: "10px 14px",
            background: "#fdf2f2",
            borderRadius: 8,
            border: "1px solid #f5c6cb",
          }}
        >
          <ErrorOutline fontSize="small" />
          <Typography variant="body2" style={{ color: "#e74c3c" }}>
            {error}
          </Typography>
        </Box>
      ) : null}

      {data ? (
        <Card variant="outlined" style={{ background: "#fafbff", borderRadius: 10 }}>
          <CardContent>
            <RenderValue value={data} />
          </CardContent>
        </Card>
      ) : null}

      <Divider style={{ margin: "20px 0" }} />
      <Typography variant="caption" color="textSecondary" style={{ display: "block", textAlign: "center" }}>
        Data powered by{" "}
        <Link href="https://open.sincera.io" target="_blank" rel="noopener" style={{ color: "#fe5000" }}>
          OpenSincera
        </Link>
      </Typography>
    </Box>
  );
}

export default function JsonExporter() {
  const [tab, setTab] = useState(0);
  const [analysisRaw, setAnalysisRaw] = useState("");
  const [analysisError, setAnalysisError] = useState("");
  const [analysisSummary, setAnalysisSummary] = useState(null);
  const [validateRaw, setValidateRaw] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationSuccess, setValidationSuccess] = useState(false);

  function handleAnalyse() {
    setAnalysisError("");

    try {
      const parsed = parseSellersJson(analysisRaw);
      setAnalysisSummary(buildSummary(parsed));
    } catch (error) {
      setAnalysisSummary(null);
      setAnalysisError(error.message || "The sellers.json file could not be analysed.");
    }
  }

  function handleExport() {
    setAnalysisError("");

    try {
      const parsed = parseSellersJson(analysisRaw);
      exportSellersCsv(parsed);
    } catch (error) {
      setAnalysisError(error.message || "The sellers.json file could not be exported.");
    }
  }

  function handleValidate() {
    try {
      const parsed = parseSellersJson(validateRaw);
      const errors = validateSellerEntries(parsed);
      setValidationErrors(errors);
      setValidationSuccess(errors.length === 0);
    } catch (error) {
      setValidationErrors([error.message || "The sellers.json file could not be validated."]);
      setValidationSuccess(false);
    }
  }

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, nextTab) => setTab(nextTab)}
        indicatorColor="secondary"
        textColor="secondary"
        style={{ borderBottom: "1px solid #e0e0e0", marginBottom: 16 }}
      >
        <Tab label="Analyse & Export" />
        <Tab label="Validate" />
        <Tab label="OpenSincera API" />
        <Tab label="Supply Chain Audit" />
      </Tabs>

      {tab === 0 ? (
        <Box>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>
                Analyse Sellers.json
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Paste the full contents of a sellers.json file to analyse seller counts, duplicate IDs, and export clean CSV rows.
              </Typography>
              <TextField
                multiline
                rows={10}
                fullWidth
                variant="outlined"
                value={analysisRaw}
                onChange={(event) => setAnalysisRaw(event.target.value)}
                placeholder={'{\n  "version": "1.0",\n  "sellers": [\n    { "seller_id": "...", "seller_type": "PUBLISHER" }\n  ]\n}'}
                inputProps={{
                  style: {
                    fontFamily: '"Cascadia Code", Consolas, monospace',
                    fontSize: 12,
                  },
                }}
              />

              {analysisError ? (
                <Box
                  display="flex"
                  alignItems="center"
                  style={{
                    gap: 8,
                    color: "#e74c3c",
                    marginTop: 12,
                    padding: "10px 14px",
                    background: "#fdf2f2",
                    borderRadius: 8,
                    border: "1px solid #f5c6cb",
                  }}
                >
                  <ErrorOutline fontSize="small" />
                  <Typography variant="body2" style={{ color: "#e74c3c" }}>
                    {analysisError}
                  </Typography>
                </Box>
              ) : null}

              <Box display="flex" style={{ gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <Button variant="contained" color="primary" startIcon={<CheckCircle />} onClick={handleAnalyse}>
                  Analyse
                </Button>
                <Button variant="outlined" color="secondary" startIcon={<CloudDownload />} onClick={handleExport}>
                  Export CSV
                </Button>
              </Box>
            </CardContent>
          </Card>

          {analysisSummary ? (
            <Box style={{ marginTop: 20 }}>
              <Box display="flex" flexWrap="wrap" style={{ gap: 10, marginBottom: 20 }}>
                <StatCard color="#636e72" label="Total Sellers" value={analysisSummary.totalSellers} />
                <StatCard color={analysisSummary.duplicateIds > 0 ? "#e74c3c" : "#27ae60"} label="Duplicate IDs" value={analysisSummary.duplicateIds} />
                <StatCard color={analysisSummary.duplicateNames > 0 ? "#f39c12" : "#27ae60"} label="Duplicate Names" value={analysisSummary.duplicateNames} />
                <StatCard color="#0984e3" label="Publisher" value={analysisSummary.typeRows.find((row) => row[0] === "PUBLISHER")?.[1] || 0} />
                <StatCard color="#6c5ce7" label="Intermediary" value={analysisSummary.typeRows.find((row) => row[0] === "INTERMEDIARY")?.[1] || 0} />
                <StatCard color="#00b894" label="Both" value={analysisSummary.typeRows.find((row) => row[0] === "BOTH")?.[1] || 0} />
              </Box>

              <MUIDataTable title="Seller Types" data={analysisSummary.typeRows} columns={["Type", "Instances"]} options={tableOptions("SellerTypes")} />
              <Box mt={2} />
              <MUIDataTable title="Seller IDs" data={analysisSummary.idRows} columns={["Seller ID", "Instances"]} options={tableOptions("SellerIds")} />
              <Box mt={2} />
              <MUIDataTable title="Company Count" data={analysisSummary.nameRows} columns={["Company", "Instances"]} options={tableOptions("CompanyCount")} />
              <Box mt={2} />
              <MUIDataTable title="Domain Count" data={analysisSummary.domainRows} columns={["Domain", "Instances"]} options={tableOptions("DomainCount")} />
            </Box>
          ) : null}
        </Box>
      ) : null}

      {tab === 1 ? (
        <Box>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>
                Validate Sellers.json
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Check each seller entry for missing fields, invalid seller types, confidentiality mismatches, and duplicate seller IDs.
              </Typography>
              <TextField
                multiline
                rows={10}
                fullWidth
                variant="outlined"
                value={validateRaw}
                onChange={(event) => setValidateRaw(event.target.value)}
                placeholder="Paste sellers.json content here"
                inputProps={{
                  style: {
                    fontFamily: '"Cascadia Code", Consolas, monospace',
                    fontSize: 12,
                  },
                }}
              />

              <Button variant="contained" color="primary" startIcon={<CheckCircle />} onClick={handleValidate} style={{ marginTop: 16 }}>
                Validate
              </Button>
            </CardContent>
          </Card>

          {validationSuccess ? (
            <Box
              display="flex"
              alignItems="center"
              style={{
                gap: 10,
                marginTop: 16,
                padding: "14px 20px",
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 10,
              }}
            >
              <CheckCircle style={{ color: "#27ae60" }} />
              <Typography style={{ color: "#27ae60", fontWeight: 700 }}>
                No validation errors were found. The sellers.json payload looks good.
              </Typography>
            </Box>
          ) : null}

          {validationErrors.length > 0 ? (
            <Card variant="outlined" style={{ marginTop: 16 }}>
              <CardContent>
                <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
                  <ErrorOutline style={{ color: "#e74c3c" }} />
                  <Typography variant="h6" style={{ color: "#e74c3c", fontWeight: 700 }}>
                    {validationErrors.length} validation error{validationErrors.length === 1 ? "" : "s"} found
                  </Typography>
                </Box>

                <Box display="grid" style={{ gap: 8, maxHeight: 420, overflowY: "auto" }}>
                  {validationErrors.map((error, index) => (
                    <Box
                      key={`${error}-${index}`}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        background: "#fff5f5",
                        border: "1px solid #fed7d7",
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          color: "#c0392b",
                          fontFamily: '"Cascadia Code", Consolas, monospace',
                          fontSize: 12,
                        }}
                      >
                        {error}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          ) : null}
        </Box>
      ) : null}

      {tab === 2 ? (
        <Card variant="outlined">
          <CardContent>
            <OpenSinceraExplorer />
          </CardContent>
        </Card>
      ) : null}

      {tab === 3 ? <SchainAudit /> : null}
    </Box>
  );
}
